import React, { cloneElement, Children, Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import generateClassesFromMap from '@insight/toolkit-utils/lib/generators/generateClassesFromMap';
import generateUniqueId from '@insight/toolkit-utils/lib/generators/generateUniqueId';
import preventScrolling from '@insight/toolkit-utils/lib/display/preventScrolling';

import FocusTrap from '../Accessibility/FocusTrap/FocusTrap';
import Portal from '../Portal/Portal';
import ModalHeader from './ModalHeader';
import ModalBody from './ModalBody';
import ModalFooter from './ModalFooter';
import { MODAL_SIZES, MODAL_CLASS_MAP } from './classes';

export default class Modal extends Component {
  static Header = ModalHeader;

  static Body = ModalBody;

  static Footer = ModalFooter;

  constructor(props) {
    super(props);

    this.applicationNode = this.getApplicationNode();
    this.closingTransitionTimeout = undefined;
    this.scrollLock = preventScrolling();
    this.state = {
      isClosing: false,
    };
  }

  componentDidMount() {
    const { closeOnEsc, isOpen, onEnter, scrollDisabled } = this.props;
    if (onEnter) {
      onEnter(this.modal);
    }

    // Timeout to ensure this happens after focus has moved
    const ariaTimeout = setTimeout(() => {
      if (this.applicationNode) {
        this.applicationNode.setAttribute('aria-hidden', 'true');
      }
      clearTimeout(ariaTimeout);
    }, 0);

    if (closeOnEsc) {
      document.addEventListener('keydown', this.checkKeyDown);
    }

    if (scrollDisabled && isOpen) {
      this.scrollLock.on();
    }
  }

  componentDidUpdate(prevProps) {
    const { isOpen, scrollDisabled } = prevProps;
    if (!isOpen && this.props.isOpen) {
      clearTimeout(this.closingTransitionTimeout);
    }

    if (scrollDisabled && !this.props.scrollDisabled) {
      this.scrollLock.off();
    } else if (!scrollDisabled && this.props.scrollDisabled) {
      this.scrollLock.on();
    }
  }

  componentWillUnmount() {
    if (this.props.scrollDisabled) {
      this.scrollLock.off();
    }
    if (this.applicationNode) {
      this.applicationNode.setAttribute('aria-hidden', false);
    }

    document.removeEventListener('keydown', this.checkKeyDown);
    clearTimeout(this.closingTransitionTimeout);
  }

  getApplicationNode = () => {
    const { getApplicationNode } = this.props;
    if (getApplicationNode) {
      if (typeof getApplicationNode === 'function') {
        return getApplicationNode();
      }
      return document.getElementById(getApplicationNode);
    }
    return null;
  };

  handleClose = () => {
    this.setState({ isClosing: true });
    this.closingTransitionTimeout = setTimeout(() => {
      this.props.onClose();
    }, this.props.closeTimeout);
  };

  checkKeyDown = (e) => {
    if (e.key === 'Escape' || e.key === 'Esc') {
      this.handleClose();
    }
    // if event is bubbled up from form element in modal, do not close modal
    if (
      e.keyCode === 37 &&
      !['input', 'textarea'].includes(e.target.localName)
    ) {
      this.handleClose();
    }
  };

  checkOverlayClick = ({ target }) => {
    if (this.props.closeOnOutsideClick) {
      if (this.modal && this.modal.contains(target)) {
        return;
      }
      this.handleClose();
    }
  };

  renderModalChildren = (contentId, headingId) => {
    const { children, isLoading } = this.props;
    return Children.map(children, (child) => {
      if (child.type && child.type.part) {
        const { part } = child.type;
        if (part === 'modal-header') {
          return cloneElement(child, {
            id: headingId,
            onClick: this.handleClose,
          });
        }
        if (part === 'modal-body') {
          return cloneElement(child, {
            id: contentId,
            isLoading,
          });
        }
      }
      return child;
    });
  };

  setModalRef = (node) => {
    this.modal = node;
  };

  render() {
    const {
      children,
      className,
      closeOnEsc,
      closeOnOutsideClick,
      closeTimeout,
      contentId = generateUniqueId(),
      focusTrapOptions,
      focusTrapPaused,
      getApplicationNode,
      headingId = getApplicationNode(),
      initialFocus,
      isLoading,
      isOpen,
      mountNode,
      onClose,
      onEnter,
      scrollDisabled,
      size,
      ...rest
    } = this.props;

    const derivedClasses = generateClassesFromMap({ size }, MODAL_CLASS_MAP);
    const classes = cn(
      'c-modal',
      derivedClasses,
      {
        'is-closing': this.state.isClosing,
        'is-open': isOpen,
      },
      className
    );

    const modal = (
      <FocusTrap
        focusTrapOptions={{
          clickOutsideDeactivates: true,
          escapeDeactives: closeOnEsc,
          initialFocus: initialFocus ? `#${initialFocus}` : () => this.modal,
          onDeactive: this.handleClose,
          ...(focusTrapOptions || {}),
        }}
      >
        <div
          className={classes}
          onClick={this.checkOverlayClick}
          data-testid="modal-overlay"
        >
          <div
            aria-describedby={contentId}
            aria-labelledby={headingId}
            aria-modal="true"
            className="c-modal__container"
            ref={this.setModalRef}
            role="dialog"
            tabIndex={0}
            {...rest}
          >
            {this.renderModalChildren(contentId, headingId)}
          </div>
        </div>
      </FocusTrap>
    );

    // TODO: Refactor this to an (IS_TEST) block and use a babel plugin to strip
    // this call completely in prod
    if (process?.env?.NODE_ENV === 'test' || process?.env?.CI === true) {
      return modal;
    }
    return <Portal mountNode={mountNode}>{modal}</Portal>
  }
}

/* eslint-disable react/require-default-props */
Modal.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  closeOnEsc: PropTypes.bool,
  closeOnOutsideClick: PropTypes.bool,
  closeTimeout: PropTypes.number,
  contentId: PropTypes.string,
  focusTrapOptions: PropTypes.shape({}),
  focusTrapPaused: PropTypes.bool,
  getApplicationNode: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  headingId: PropTypes.string,
  initialFocus: PropTypes.string,
  isLoading: PropTypes.bool,
  isOpen: PropTypes.bool,
  mountNode: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.node,
    PropTypes.object,
  ]),
  onClose: PropTypes.func.isRequired,
  onEnter: PropTypes.func,
  scrollDisabled: PropTypes.bool,
  size: PropTypes.oneOf(MODAL_SIZES),
};

Modal.defaultProps = {
  children: null,
  className: '',
  closeOnEsc: true,
  closeOnOutsideClick: true,
  closeTimeout: 250,
  contentId: '',
  focusTrapPaused: false,
  headingId: '',
  isLoading: false,
  isOpen: false,
  scrollDisabled: true,
};
/* eslint-enable react/require-default-props */
